var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import { KeyboardInjectorFinder } from '@inter-ikea-kompis/component-keyboard';
import { SheetBody, SheetBodyPadding, SheetFooter, SheetHeader } from '@inter-ikea-kompis/component-sheet';
import { Sheet } from '@inter-ikea-kompis/component-sheet';
import '@inter-ikea-kompis/component-focus-outline';
import FinancialServicesModalEnum from '../../enums/FinancialServicesModalEnum.js';
import FinancialServicesTracker from '../../trackers/FinancialServicesTracker.js';
import FinancialServicesButtons from '../financial-services-buttons/FinancialServicesButtons.js';
import FinancialServicesCard from '../financial-services-card/FinancialServicesCard.js';
import FinancialServicesLink from '../financial-services-link/FinancialServicesLink.js';
import FinancialServicesStyle from './FinancialServicesStyle.js';
/**
 * Financial services.
 *
 * @event modalClose Modal close.
 * @event modalOpen Modal open.
 * @event continueButtonClick When continue planning button is clicked in sheet.
 * @event moreInfoClick When read more is clicked in sheet or direct link is clicked.
 *
 * @tracker packages/component-financial-services/src/trackers/FinancialServicesTracker.ts
 */
export default class FinancialServices extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Visible modal.
        this.visibleModal = null;
        // Disables links. Shows QR code. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        this.sheetElement = null;
        this.cardElement = null;
        this.buttonsElement = null;
        this.tracker = null;
        this.dexfSettings = null;
    }
    render() {
        // We want translations and settings to be required, but since the props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${FinancialServicesStyle.getStyle({
            theme: this.theme
        })}
			</style>
			<div class="container">
				<kompis-financial-services-link
					.financingOption="${this.financingOption}"
					.theme="${this.theme}"
					.disableLinks="${this.disableLinks}"
					@linkClick="${this.financingOption.anchorLink
            ? this.onDirectLinkClick.bind(this)
            : this.onLinkClick.bind(this)}"
				>
				</kompis-financial-services-link>
			</div>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-sheet', Sheet);
        this.customElements.define('kompis-sheet-header', SheetHeader);
        this.customElements.define('kompis-sheet-body', SheetBody);
        this.customElements.define('kompis-sheet-footer', SheetFooter);
        this.customElements.define('kompis-sheet-body-padding', SheetBodyPadding);
        this.customElements.define('kompis-financial-services-card', FinancialServicesCard);
        this.customElements.define('kompis-financial-services-link', FinancialServicesLink);
        this.customElements.define('kompis-financial-services-buttons', FinancialServicesButtons);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.disconnectTracker();
        this.closeSheet();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('visibleModal') && this.hasTranslationsAndSettings()) {
            if (this.visibleModal === FinancialServicesModalEnum.financialServicesCard) {
                this.openSheet();
            }
            else {
                this.closeSheet();
            }
        }
        if (this.cardElement) {
            this.cardElement.theme = this.theme;
            this.cardElement.financingOption = this.financingOption;
        }
        if (this.buttonsElement) {
            this.buttonsElement.financingOption = this.financingOption;
            this.buttonsElement.theme = this.theme;
            this.buttonsElement.localizedInformation = this.localizedInformation;
            this.buttonsElement.translations = this.translations;
            this.buttonsElement.disableLinks = this.disableLinks;
        }
        if (changedProperties.has('analytics') && this.hasTranslationsAndSettings()) {
            this.disconnectTracker();
            this.connectTracker();
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation?.translations;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    async connectTracker() {
        if (this.analytics) {
            const reporter = this.analytics.__INTERNAL__reporter;
            if (reporter?.connected === false) {
                // Should use an existing reporter, but wait for it to be connected
                return;
            }
            // Use existing connected reporter, or the tracker should create a new one.
            const reporterId = reporter ? reporter.id : undefined;
            this.tracker = new FinancialServicesTracker({
                ...this.analytics,
                settings: this.getSettings(),
                reporterId
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    onLinkClick(event) {
        this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: FinancialServicesModalEnum.financialServicesCard,
                triggerElement: event.detail.triggerElement
            }
        }));
        this.tracker?.sendOpenSheet();
    }
    onDirectLinkClick(event) {
        this.dispatchEvent(new CustomEvent('moreInfoClick', {
            bubbles: true,
            detail: {
                visibleModal: null,
                triggerElement: event.detail.triggerElement
            }
        }));
        this.tracker?.sendMoreInfoClick();
    }
    closeSheet() {
        if (this.sheetElement) {
            this.sheetElement.visible = false;
            this.sheetElement = null;
            this.cardElement = null;
            this.buttonsElement = null;
        }
    }
    openSheet() {
        if (this.sheetElement) {
            return;
        }
        const sheetElement = this.createSheetElement();
        const onClose = () => {
            if (this.sheetElement) {
                this.dispatchEvent(new CustomEvent('modalClose', {
                    bubbles: true,
                    detail: {
                        visibleModal: null,
                        triggerElement: this.getTriggerElement()
                    }
                }));
            }
        };
        // When user closes the Sheet
        sheetElement.addEventListener('overlayClick', onClose);
        const sheetHeaderElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-sheet-header'));
        sheetHeaderElement?.addEventListener('closeButtonClick', onClose);
        // Cleanup sheet when the animation is over
        sheetElement.addEventListener('animationEnd', () => {
            if (!sheetElement.visible) {
                sheetElement.parentElement?.removeChild(sheetElement);
            }
        });
        this.sheetElement = sheetElement;
        this.cardElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-financial-services-card'));
        this.buttonsElement = sheetElement.querySelector(this.customElements.getInternalName('kompis-financial-services-buttons'));
        this.buttonsElement?.addEventListener('continueButtonClick', (event) => {
            this.dispatchEvent(new CustomEvent('continueButtonClick', {
                detail: {
                    ...event.detail,
                    triggerElement: this.getTriggerElement()
                },
                bubbles: true
            }));
        });
        this.buttonsElement?.addEventListener('moreInfoClick', (event) => {
            this.dispatchEvent(new CustomEvent('moreInfoClick', {
                detail: { ...event.detail },
                bubbles: true
            }));
            this.tracker?.sendMoreInfoClick();
        });
        sheetElement.visible = true;
        const parent = KeyboardInjectorFinder.findKeyboardInjectorElement() || document.body;
        parent.appendChild(sheetElement);
    }
    createSheetElement() {
        const sheetElement = this.createInternalElement('kompis-sheet');
        const sheetHeaderElement = this.createInternalElement('kompis-sheet-header');
        const sheetBodyElement = this.createInternalElement('kompis-sheet-body');
        const sheetPaddingElement = this.createInternalElement('kompis-sheet-body-padding');
        const cardElement = this.createInternalElement('kompis-financial-services-card');
        const sheetFooterElement = this.createInternalElement('kompis-sheet-footer');
        const buttonsElement = this.createInternalElement('kompis-financial-services-buttons');
        sheetElement.theme = this.theme;
        sheetBodyElement.theme = this.theme;
        sheetPaddingElement.theme = this.theme;
        sheetPaddingElement.style.paddingBottom = this.theme.spacing.space150 / 16 + 'rem';
        sheetHeaderElement.theme = this.theme;
        sheetHeaderElement.ariaLabelCloseButton = this.getTranslations().modalAriaLabelButtonClose;
        cardElement.financingOption = this.financingOption;
        cardElement.theme = this.theme;
        sheetFooterElement.theme = this.theme;
        buttonsElement.financingOption = this.financingOption;
        buttonsElement.theme = this.theme;
        buttonsElement.localizedInformation = this.localizedInformation;
        buttonsElement.translations = this.translations;
        buttonsElement.disableLinks = this.disableLinks;
        sheetElement.appendChild(sheetHeaderElement);
        sheetElement.appendChild(sheetBodyElement);
        sheetBodyElement.appendChild(sheetPaddingElement);
        sheetPaddingElement.appendChild(cardElement);
        sheetElement.appendChild(sheetFooterElement);
        sheetFooterElement.appendChild(buttonsElement);
        return sheetElement;
    }
    getTriggerElement() {
        const financialServicesLink = this.shadowRoot?.querySelector(this.customElements.getInternalName('kompis-financial-services-link'));
        const hyperLink = financialServicesLink?.shadowRoot.querySelector(financialServicesLink?.customElements.getInternalName('kompis-hyperlink'));
        const focusOutline = hyperLink?.shadowRoot?.querySelector(hyperLink?.customElements.getInternalName('kompis-focus-outline'));
        return focusOutline?.querySelector('a') || null;
    }
}
__decorate([
    property({ type: Object, required: true })
], FinancialServices.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], FinancialServices.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], FinancialServices.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], FinancialServices.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], FinancialServices.prototype, "financingOption", void 0);
__decorate([
    property({ type: String })
], FinancialServices.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], FinancialServices.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Object })
], FinancialServices.prototype, "analytics", void 0);
//# sourceMappingURL=FinancialServices.js.map